import { createContext, useContext, ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

type Language = "sv" | "en";

interface LanguageContextType {
  language: Language;
  switchLanguage: (lang: Language) => void;
  switchPath: (lang: Language) => string;
  t: (sv: string, en: string) => string;
  localePath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const language: Language = lang === "en" ? "en" : "sv";

  // Samma sida på det andra språket: byt bara språkprefixet, behåll query/hash.
  // Används som href i språkväxlaren så att sv/en hänger ihop med riktiga <a>-länkar
  // (crawlers följer inte onClick/navigate).
  const switchPath = (newLang: Language) => {
    const pathWithoutLang = location.pathname.replace(/^[/](sv|en)/, "");
    return `/${newLang}${pathWithoutLang}${location.search}${location.hash}`;
  };

  const switchLanguage = (newLang: Language) => {
    navigate(switchPath(newLang));
  };

  const t = (sv: string, en: string) => (language === "en" ? en : sv);

  // Startsidan är "/sv" resp. "/en" utan avslutande snedstreck – samma form som canonical
  // och sitemap. "/sv/" matchar ingen fil i SSG-outputen (dist/sv.html) och gav dubbla URL:er.
  const localePath = (path: string) => `/${language}${path === "/" ? "" : path}`;

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, switchPath, t, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
